import { useCallback, useEffect, useState } from 'react'
import { listStaff, renameOwner, type StaffMember } from '../../services/superadmin'
import { PanelGate } from './PanelGate'
import { Spinner } from '../Spinner'
import { listStaffActivity } from '../../services/superadmin'
import { ago, freshness } from '../../utils/ago'

const ROLE_LABEL: Record<string, string> = {
  owner: 'Patron',
  cashier: 'Kasiyer',
  waiter: 'Garson',
}

/**
 * Who works at this institution, and a way to change the owner's own login.
 *
 * Cashiers and waiters are shown but not touched here, that is the owner's
 * own job, from their own Ayarlar screen (services/staff.ts). The one thing
 * only the system administrator can do is change the *owner's* credentials:
 * an owner who is locked out has no one else in the institution who can help
 * them, and functions/staff's `reset` action already refuses to touch an
 * owner row unless the caller is the system administrator, this is simply
 * the screen that reaches that door.
 *
 * Only the shown name. Credentials are the owner's own now: they hold a real
 * address, so a forgotten password is answered by the reset mail on the
 * sign-in screen rather than by setting one from here.
 */
export function StaffPanel({ tenantId }: { tenantId: string }) {
  const [staff, setStaff] = useState<StaffMember[]>([])
  /** Kişi kişi son görülme; yüklenemezse liste yine görünür. */
  const [seen, setSeen] = useState<Record<string, { seen: string | null; login: string | null }>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [target, setTarget] = useState<StaffMember | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [showGate, setShowGate] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  /**
   * Re-read after a save as well as on arrival: the row the panel shows is a
   * copy taken when the list loaded, so without this a renamed owner kept
   * their old name on screen until the whole tenant was reselected.
   */
  const reload = useCallback(async () => {
    setError(null)
    try {
      setStaff(await listStaff(tenantId))
      listStaffActivity(tenantId).then(setSeen).catch(() => setSeen({}))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Personel yüklenemedi.')
    }
  }, [tenantId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setTarget(null)
    setDone(false)
    reload().finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [reload])

  function startReset(member: StaffMember) {
    setTarget(member)
    setDisplayName(member.displayName)
    setResetError(null)
    setDone(false)
  }

  async function onConfirmed(secret: string) {
    if (!target) return
    setResetting(true)
    setResetError(null)
    try {
      await renameOwner(secret, target.uid, displayName)
      setDone(true)
      setTarget(null)
      setShowGate(false)
      await reload()
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Sıfırlanamadı.')
      setShowGate(false)
    } finally {
      setResetting(false)
    }
  }

  if (loading) return <Spinner label="Personel yükleniyor…" />
  if (error) return <p className="superadmin__error">{error}</p>

  return (
    <div className="staff-panel">
      <h4>Personel</h4>
      {staff.length === 0 && <p className="superadmin__hint">Henüz personel yok.</p>}

      <ul className="staff-panel__list">
        {staff.map((m) => (
          <li key={m.uid} className="staff-panel__row">
            <span className="staff-panel__name">{m.displayName || m.username}</span>
            <span className="staff-panel__role">{ROLE_LABEL[m.role] ?? m.role}</span>
            <span
              className="staff-panel__seen"
              title={`Şifreyle son giriş: ${ago(seen[m.uid]?.login)}`}
            >
              <i className={`tenant-list__dot is-${freshness(seen[m.uid]?.seen)}`} />
              {ago(seen[m.uid]?.seen)}
            </span>
            {m.role === 'owner' && (
              <button type="button" className="btn btn--outline btn--sm" onClick={() => startReset(m)}>
                Düzenle
              </button>
            )}
          </li>
        ))}
      </ul>

      {done && <p className="superadmin__success">Patron bilgileri güncellendi.</p>}

      {target && (
        <div className="staff-panel__reset">
          <p className="superadmin__hint">
            Ekranda ve fişte görünen isim. Giriş bilgilerini buradan
            değiştiremezsiniz; patron şifresini kendi e-postasından
            sıfırlar.
          </p>
          <label>
            Adı soyadı
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
          {resetError && <p className="superadmin__error">{resetError}</p>}
          <div className="staff-panel__reset-actions">
            <button type="button" className="btn btn--outline" onClick={() => setTarget(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={resetting || displayName.trim() === target.displayName}
              onClick={() => setShowGate(true)}
            >
              {resetting ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      )}

      {showGate && target && (
        <PanelGate
          title={`"${target.displayName || target.username}" hesabını güncelle`}
          onConfirm={onConfirmed}
          onCancel={() => setShowGate(false)}
        />
      )}
    </div>
  )
}
